/**
 * The close-out ("Finish this person") as one form, shared by the Finish sheet and the API: its shape,
 * the crew's pre-filled defaults, the list's own validation said in plain words (as i18n keys), and the
 * mapping to the Queue 3 snapshot the processor reads. Pure functions, no dependencies beyond the
 * vocabulary, so the app, the API and the tests agree.
 *
 * Two switches, any order (REQUIREMENTS.md §3):
 *   Incident        "what happened, be descriptive" → the INT_HK_INCIDENT memo; recorded on the
 *                   completion as TenantInteractionConcern + TenantInteractionDescription.
 *   Unit condition  the unit condition assessment (SM_UnitConditionAssessments), the incident report
 *                   in five numbered groups → the INT_UNIT_SAFETY_ALERT memo.
 */
import { vocab } from './vocab';
import type {
  CompletionFollowUpCategory,
  ConditionLevel,
  FollowUpPriority,
  ProcessorSnapshot,
  ServiceOutcome,
} from './envelopes/queue3';

const A = vocab.SM_UnitConditionAssessments;
export type ConditionCategory = (typeof A.PrimaryConditionCategory)[number];
export type Severity = (typeof A.AssessmentSeverityLevel)[number];
export type Effect = (typeof A.EffectOnScheduledService)[number];
export type AssessmentFollowUpCategory = Exclude<(typeof A.FollowUpCategory)[number], 'Not Applicable'>;
export type Priority = Exclude<FollowUpPriority, 'Not Applicable'>;

export const CONDITION_CATEGORIES = A.PrimaryConditionCategory;
export const SEVERITIES = A.AssessmentSeverityLevel;
export const EFFECTS = A.EffectOnScheduledService;
export const PRIORITIES: readonly Priority[] = ['Routine', 'Prompt', 'Urgent', 'Immediate'];
/** The reference's order for "What kind" (Maintenance first). */
export const ASSESSMENT_FOLLOW_UP_CATEGORIES: readonly AssessmentFollowUpCategory[] = [
  'Maintenance or Repair',
  'Additional Housekeeping',
  'Unit Condition',
  'Pest Control',
  'Biohazard Remediation',
  'Safety',
  'Tenant Support',
  'Program Support',
  'Other',
];
/** "Anything else you saw?" chips — every category (the reference shows the common ones first). */
export const OBSERVED_CHIPS: readonly ConditionCategory[] = CONDITION_CATEGORIES;
export const CONDITION_LEVELS: readonly ConditionLevel[] = ['Routine - Nothing to Report', 'Elevated Cleaning Need', 'Potential Health or Safety Concern'];
export const SERVICE_OUTCOMES: readonly ServiceOutcome[] = vocab.SM_ServiceCompletions.ServiceOutcome;
export const COMPLETION_FOLLOW_UP_CATEGORIES = vocab.SM_ServiceCompletions.FollowUpCategory.filter((c) => c !== 'Not Applicable') as Exclude<
  CompletionFollowUpCategory,
  'Not Applicable'
>[];

/** Half-hour steps for "How long did it take?" (30 min … 4 h). */
export const DURATION_STEPS = [30, 60, 90, 120, 150, 180, 210, 240] as const;
export const MAX_WORKERS = 4;

export interface AssessmentForm {
  primary: ConditionCategory;
  observed: ConditionCategory[];
  otherDescription: string;
  severity: Severity;
  immediateDanger: boolean;
  serviceStopped: boolean;
  supervisorContacted: boolean;
  buildingManagementContacted: boolean;
  emergencyContacted: boolean;
  observationSummary: string;
  areasAffected: string;
  effect: Effect;
  escalationNotes: string;
  followUpRecommended: boolean;
  followUpCategory: AssessmentFollowUpCategory;
  followUpPriority: Priority;
  recommendedFollowUp: string;
  /** Library paths of the photos uploaded for the report (Unit condition folder). */
  photos: string[];
  tenantAgreedPhotos: boolean;
  photoNotes: string;
}

export interface CloseoutForm {
  /** The Finish questions version the crew saw (`_app/finish-questions.json`). */
  formVersion: string;
  /** The language the sheet was shown in. */
  language: 'en' | 'es';
  /** Rooms checklist: question key → ticked. */
  rooms: Record<string, boolean>;
  /** Standard-clean questions: key → yes / no. */
  standard: Record<string, boolean>;
  typeOfClean: 'standard' | 'deep';
  durationMinutes: number | null;
  workers: number;
  outcome: ServiceOutcome;
  level: ConditionLevel;
  /** Elevated Cleaning Need adds a follow-up (FollowUpRequired, category, priority). */
  followUp: { required: boolean; category: Exclude<CompletionFollowUpCategory, 'Not Applicable'>; priority: Priority; summary: string };
  notes: string;
  /** Library paths of the visit photos (History folder). */
  photos: string[];
  incident: { on: boolean; what: string };
  unitCondition: AssessmentForm | null;
  /** Queue the tenant's Complaint and Feedback link (when the tenant has an email). */
  sendFeedback: boolean;
}

export function defaultAssessment(): AssessmentForm {
  return {
    primary: 'Structural concern',
    observed: [],
    otherDescription: '',
    severity: 'Potential Health or Safety Concern',
    immediateDanger: false,
    serviceStopped: false,
    supervisorContacted: false,
    buildingManagementContacted: false,
    emergencyContacted: false,
    observationSummary: '',
    areasAffected: '',
    effect: 'No Effect',
    escalationNotes: '',
    followUpRecommended: true,
    followUpCategory: 'Maintenance or Repair',
    followUpPriority: 'Prompt',
    recommendedFollowUp: '',
    photos: [],
    tenantAgreedPhotos: false,
    photoNotes: '',
  };
}

/**
 * The pre-filled form. For everyone: Standard (17 Oct 2026: Deep when the visit was booked as a deep clean), the
 * scheduled length, 1 worker, Completed, Routine, both switches off. Every room ticked (the crew's one-press default,
 * REQUIREMENTS.md §4 item 2).
 */
export function defaultCloseout(a: {
  roomKeys: string[];
  /** Standard-clean question key → its pre-filled answer (from the Finish questions file). */
  standard: Record<string, boolean>;
  scheduledMinutes: number | null;
  formVersion: string;
  language: 'en' | 'es';
  /** 17 Oct 2026: the type of clean the visit was booked as (FinishConfig.appointment.bookedTypeOfClean); absent = standard. */
  typeOfClean?: CloseoutForm['typeOfClean'];
}): CloseoutForm {
  const dur = a.scheduledMinutes && a.scheduledMinutes > 0 ? nearestStep(a.scheduledMinutes) : 60;
  return {
    formVersion: a.formVersion,
    language: a.language,
    rooms: Object.fromEntries(a.roomKeys.map((k) => [k, true])),
    standard: { ...a.standard },
    typeOfClean: a.typeOfClean === 'deep' ? 'deep' : 'standard',
    durationMinutes: dur,
    workers: 1,
    outcome: 'Completed',
    level: 'Routine - Nothing to Report',
    followUp: { required: false, category: 'Additional Housekeeping', priority: 'Routine', summary: '' },
    notes: '',
    photos: [],
    incident: { on: false, what: '' },
    unitCondition: null,
    sendFeedback: false,
  };
}

export function nearestStep(minutes: number): number {
  let best: number = DURATION_STEPS[0];
  for (const s of DURATION_STEPS) if (Math.abs(s - minutes) < Math.abs(best - minutes)) best = s;
  return best;
}

/** The completion's condition level follows the report when the Unit condition switch is on. */
export function levelFromSeverity(s: Severity): ConditionLevel {
  return s === 'Elevated Cleaning Need' ? 'Elevated Cleaning Need' : 'Potential Health or Safety Concern';
}

/**
 * The assessment's follow-up categories are wider than the completion's (the processor refuses a value
 * outside its own list). The completion carries the nearest one (docs/DECISIONS.md D-0.4.0-6).
 */
export function completionCategoryFor(c: AssessmentFollowUpCategory): Exclude<CompletionFollowUpCategory, 'Not Applicable'> {
  switch (c) {
    case 'Pest Control':
      return 'Unit Condition';
    case 'Biohazard Remediation':
      return 'Safety';
    case 'Tenant Support':
      return 'Program Support';
    default:
      return c;
  }
}

/** One problem, as an i18n key under `finish.invalid.` and the field it points at (for focus). */
export interface Invalid {
  key: string;
  field: string;
}

/**
 * The list's own validation said in plain words, before submit (SM_UnitConditionAssessments and
 * SM_ServiceCompletions formulas, reference/SCHEMA-DIGEST.md), plus what the sheet itself needs.
 */
export function validateCloseout(f: CloseoutForm): Invalid[] {
  const out: Invalid[] = [];
  if (f.durationMinutes !== null && (f.durationMinutes < 0 || f.durationMinutes > 24 * 60)) out.push({ key: 'duration', field: 'took' });
  if (f.workers < 1 || f.workers > MAX_WORKERS) out.push({ key: 'workers', field: 'workers' });
  if (f.incident.on && f.incident.what.trim().length < 3) out.push({ key: 'incidentWhat', field: 'hk-what' });
  if (f.level === 'Elevated Cleaning Need' && !f.unitCondition && !f.followUp.required) out.push({ key: 'elevatedFollowUp', field: 'fu-required' });
  if (f.level === 'Potential Health or Safety Concern' && !f.unitCondition) out.push({ key: 'safetyNeedsReport', field: 'unit-condition' });
  const u = f.unitCondition;
  if (u) {
    if (!u.observationSummary.trim()) out.push({ key: 'summary', field: 'idesc' });
    if (u.primary === 'Other observable concern' && !u.otherDescription.trim()) out.push({ key: 'otherWhat', field: 'i-other' });
    if (u.immediateDanger && !u.supervisorContacted) out.push({ key: 'dangerSupervisor', field: 'yn-super' });
    if (u.severity === 'Immediate Danger' && !u.immediateDanger) out.push({ key: 'dangerPresent', field: 'yn-danger' });
    if (u.severity === 'Immediate Danger' && (!u.followUpRecommended || u.followUpPriority !== 'Immediate')) out.push({ key: 'dangerImmediate', field: 'i-fupri' });
    if (u.photos.length > 0 && !u.tenantAgreedPhotos) out.push({ key: 'photosNeedOk', field: 'i-release' });
  }
  return out;
}

/** Everything the photos say, for the completion (the visit photos, the tenant's release on file). */
export function photoFacts(f: CloseoutForm, releaseOnFile: boolean): Pick<
  ProcessorSnapshot,
  'PhotosAdded' | 'PhotoCount' | 'PhotoReleaseVerified' | 'PhotoDocumentationStatus' | 'PhotoNotesOrException'
> {
  const n = f.photos.length + (f.unitCondition?.photos.length ?? 0);
  if (n === 0) return { PhotosAdded: false, PhotoCount: 0, PhotoReleaseVerified: releaseOnFile, PhotoDocumentationStatus: 'Not Added', PhotoNotesOrException: '' };
  const agreed = releaseOnFile || !!f.unitCondition?.tenantAgreedPhotos;
  if (!agreed) {
    // Never a block at close-out: the photos are filed and the record says there is no release on file
    // (docs/DECISIONS.md D-0.4.0-5). The list only accepts PhotosAdded with a verified release.
    return {
      PhotosAdded: false,
      PhotoCount: n,
      PhotoReleaseVerified: false,
      PhotoDocumentationStatus: 'Filed',
      PhotoNotesOrException: `No photo release on file for this tenant; ${n} photo(s) filed with the visit. The report says so.`,
    };
  }
  return { PhotosAdded: true, PhotoCount: n, PhotoReleaseVerified: true, PhotoDocumentationStatus: 'Filed', PhotoNotesOrException: f.unitCondition?.photoNotes ?? '' };
}

/** The Queue 3 snapshot for a close-out: the processor's keys at the top level, the app's beside them. */
export function closeoutSnapshot(
  f: CloseoutForm,
  a: { releaseOnFile: boolean; completedAt: string; folderLink: string | null; assessmentId: string | null; startedAt?: string | null },
): ProcessorSnapshot & Record<string, unknown> {
  const u = f.unitCondition;
  const level: ConditionLevel = u ? levelFromSeverity(u.severity) : f.level;
  const followUp =
    u && u.followUpRecommended
      ? { required: true, category: completionCategoryFor(u.followUpCategory), priority: u.followUpPriority, summary: u.recommendedFollowUp }
      : f.followUp.required
        ? f.followUp
        : null;
  return {
    OutcomeNotes: f.notes,
    ConditionNotes: u ? u.observationSummary : level === 'Elevated Cleaning Need' ? f.followUp.summary : '',
    TenantInteractionConcern: f.incident.on,
    TenantInteractionDescription: f.incident.on ? f.incident.what : '',
    ActualDurationMinutes: f.durationMinutes,
    RoutineCompletion: level === 'Routine - Nothing to Report' && !f.incident.on && !u,
    AdditionalCleaningRecommended: level === 'Elevated Cleaning Need',
    ImmediateHealthSafetyConcern: level === 'Potential Health or Safety Concern',
    ImmediateAssistanceRequired: !!u && (u.immediateDanger || u.emergencyContacted),
    StaffFeltUnsafe: false,
    FollowUpRequired: !!followUp,
    FollowUpPriority: followUp ? followUp.priority : 'Not Applicable',
    FollowUpCategory: followUp ? followUp.category : 'Not Applicable',
    FollowUpSummary: followUp ? followUp.summary : '',
    ReviewRequired: f.incident.on || !!u || level !== 'Routine - Nothing to Report',
    ServiceStartedDateTime: a.startedAt ?? null,
    ServiceCompletedDateTime: a.completedAt,
    ...photoFacts(f, a.releaseOnFile),
    // the app's own keys
    DocumentationFolderLink: a.folderLink,
    checklist: { rooms: f.rooms, standard: f.standard },
    typeOfClean: f.typeOfClean,
    workers: f.workers,
    formVersion: f.formVersion,
    language: f.language,
    incident: f.incident.on,
    unitCondition: !!u,
    assessmentId: a.assessmentId,
    sendFeedback: f.sendFeedback,
  };
}

/** The level the completion records (the report's severity wins when the Unit condition switch is on). */
export function effectiveLevel(f: CloseoutForm): ConditionLevel {
  return f.unitCondition ? levelFromSeverity(f.unitCondition.severity) : f.level;
}
